/**
 * useEmployeeScheduleData.ts
 * Data layer for the Employee Schedule views (UX overhaul). One endpoint
 * (employee-bars) returns users + day-level bars + per-(user,week) capacity, so
 * the Matrix, Weekly Board and Timeline all share this hook.
 *
 * Mirrors useScheduleData (Machine Schedule): quiet reload (no spinner flash),
 * refs for synchronous optimistic reads, and primitive mutators the mutation
 * hook composes (remove/add/update + snapshot/restore for rollback).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  fetchEmployeeBars,
  type EmployeeBar,
  type EmployeeBarUser,
  type EmployeeWeekCapacityCell,
} from '../services/scheduleEmployeeBars';

export function capacityMapKey(userKey: string, weekStart: string): string {
  return `${userKey}::${weekStart}`;
}

export interface UseEmployeeScheduleDataResult {
  users: EmployeeBarUser[];
  bars: EmployeeBar[];
  capacityByKey: Map<string, EmployeeWeekCapacityCell>;
  weekStarts: string[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  setError: (m: string | null) => void;
  reload: (opts?: { quiet?: boolean }) => Promise<void>;
  // optimistic primitives
  removeBars: (keys: string[]) => void;
  addBars: (bars: EmployeeBar[]) => void;
  updateBar: (key: string, patch: Partial<EmployeeBar>) => void;
  snapshot: () => EmployeeBar[];
  restore: (bars: EmployeeBar[]) => void;
  barsRef: React.MutableRefObject<EmployeeBar[]>;
}

export function useEmployeeScheduleData(startDate: string, endDate: string): UseEmployeeScheduleDataResult {
  const [users, setUsers] = useState<EmployeeBarUser[]>([]);
  const [bars, setBars] = useState<EmployeeBar[]>([]);
  const [capacity, setCapacity] = useState<EmployeeWeekCapacityCell[]>([]);
  const [weekStarts, setWeekStarts] = useState<string[]>([]);
  const [loadingFirst, setLoadingFirst] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasLoaded = useRef(false);
  const startRef = useRef(startDate);
  const endRef = useRef(endDate);
  useEffect(() => { startRef.current = startDate; }, [startDate]);
  useEffect(() => { endRef.current = endDate; }, [endDate]);

  const barsRef = useRef<EmployeeBar[]>([]);
  useEffect(() => { barsRef.current = bars; }, [bars]);

  const reload = useCallback(async (opts?: { quiet?: boolean }) => {
    const quiet = opts?.quiet ?? false;
    if (quiet || hasLoaded.current) setRefreshing(true);
    else setLoadingFirst(true);
    setError(null);
    try {
      const data = await fetchEmployeeBars(startRef.current, endRef.current);
      setUsers(data.users);
      setBars(data.bars);
      setCapacity(data.capacity);
      setWeekStarts(data.weekStarts);
      hasLoaded.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load employee schedule.');
    } finally {
      setLoadingFirst(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload reads window from refs
  }, [startDate, endDate]);

  const capacityByKey = useMemo(() => {
    const m = new Map<string, EmployeeWeekCapacityCell>();
    for (const c of capacity) m.set(capacityMapKey(c.userKey, c.weekStart), c);
    return m;
  }, [capacity]);

  // ── optimistic primitives ──────────────────────────────────────────────────
  const removeBars = useCallback((keys: string[]) => {
    const set = new Set(keys.map((k) => k.toLowerCase()));
    setBars((cur) => cur.filter((b) => !set.has(b.assignmentKey.toLowerCase())));
  }, []);

  const addBars = useCallback((newBars: EmployeeBar[]) => {
    setBars((cur) => {
      const have = new Set(cur.map((b) => b.assignmentKey.toLowerCase()));
      return [...cur, ...newBars.filter((b) => !have.has(b.assignmentKey.toLowerCase()))];
    });
  }, []);

  const updateBar = useCallback((key: string, patch: Partial<EmployeeBar>) => {
    setBars((cur) => cur.map((b) => (b.assignmentKey.toLowerCase() === key.toLowerCase() ? { ...b, ...patch } : b)));
  }, []);

  const snapshot = useCallback(() => barsRef.current.slice(), []);
  const restore = useCallback((snap: EmployeeBar[]) => setBars(snap), []);

  return {
    users,
    bars,
    capacityByKey,
    weekStarts,
    loading: loadingFirst && !hasLoaded.current,
    refreshing,
    error,
    setError,
    reload,
    removeBars,
    addBars,
    updateBar,
    snapshot,
    restore,
    barsRef,
  };
}
